import React from 'react'
import { Outlet } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { TrainingRequestsDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RequestsDataCollection } from 'modules/trainings/components/Requests/RequestsDataCollection'
import { useRejectTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useRejectTrainingRequest'
import { handleResult } from 'modules/core/lib/handleMutation'
import { RejectModal } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/FooterReview/RejectModal'

export default function RequestsWithDataCollection() {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data, loading, error } = useQuery(TrainingRequestsDataCollectionDocument, {
    variables: {
      companyId,
    },
  })

  const { reject, loading: rejecting } = useRejectTrainingRequest()
  const [rejectModalState, setRejectModalState] = React.useState<{ show: boolean; id: string | null }>({
    show: false,
    id: null,
  })

  const canCreate = Boolean(data?.trainings._policies.trainingsRequestsCreate?.can)
  const settingsEnabled = Boolean(
    data?.trainings.settingsConnection?.nodes?.[0]?.trainingRequestsEnabled
  )

  const handleReject = (id: string) => {
    setRejectModalState({ show: true, id })
  }

  const confirmReject = async (reason: string) => {
    if (!rejectModalState.id) return

    const result = await reject({
      id: rejectModalState.id,
      companyId,
      reason,
    })

    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('trainings.training_requests.create.wrong') })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_requests.create.success_messages.rejected'),
      })
      setRejectModalState({ show: false, id: null })
    }
  }

  return (
    <ResponsivePage.Body
      loading={loading}
      error={error}
      variant="full"
      authorized={data?.trainings._policies.trainingsAccessManagementSpace?.can}
    >
      {data && (
        <RequestsDataCollection
          onReject={handleReject}
          settingsEnabled={settingsEnabled}
          canCreate={canCreate}
        />
      )}
      {rejectModalState.show && rejectModalState.id && (
        <RejectModal
          setShowRejectedModal={(show) => setRejectModalState({ show, id: show ? rejectModalState.id : null })}
          onReject={confirmReject}
          rejecting={rejecting}
        />
      )}
      <Outlet
        context={{
          requests: data?.trainings.trainingRequestsConnection.nodes,
          settingsEnabled,
        }}
      />
    </ResponsivePage.Body>
  )
}
